import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { TagsDropdown } from '@/AdminComponent/TagsDropdown';
import { ProductsDropdown } from '@/AdminComponent/ProductsDropdown';
import { InstructorDropdown } from '@/AdminComponent/InstructorDropdown';
import { FileUploadWithManualInput } from '@/AdminComponent/FileUploadWithManualInput';
import { MuxFileUpload } from '@/AdminComponent/MuxFileUpload';
import { toast } from 'react-toastify';
import { API_KEYS, FIELDS, FILE_TYPE, FILE_TYPE_OPTIONS, MESSAGES, PDF_PAGE_REGEX, STYLES, UI_LABELS } from './utils/constants';
import { CategoriesDropdown } from '@/AdminComponent/CategoriesDropdown';
import { RelatedVideosDropdown } from '@/AdminComponent/RelatedVideosDropdown';
import { newYorkMidnight } from '@/utils/helper';
import dayjs from 'dayjs';
import ReleaseSchedule from '@/AdminComponent/components/ReleaseSchedule';

const getEmptyForm = () =>
  Object.freeze({
    [FIELDS.TITLE]: '',
    [FIELDS.ABOUT]: '',
    [FIELDS.DURATION]: '',
    [FIELDS.PAGES]: '',
    [FIELDS.TAGS]: [],
    [FIELDS.THUMBNAIL_LINK]: '',
    [FIELDS.PRODUCTS]: [],
    [FIELDS.INSTRUCTOR]: '',
    [FIELDS.CATEGORY]: [],
    [FIELDS.FILE_TYPE]: FILE_TYPE.PDF,
    [FIELDS.FILE_URL]: '',
    [FIELDS.RELATED]: [],
    [FIELDS.DATE_ONLY]: newYorkMidnight(true)
  });

const isBlankRichText = (html) => !html || html === '<p><br></p>';

const pickInitialForm = (initialValues) => {
  if (!initialValues) return getEmptyForm();

  const initialType =
    initialValues?.[FIELDS.FILE_TYPE] ??
    initialValues?.file_type ??
    FILE_TYPE.VIDEO;

  const getReleaseDate = () => {
    const timestamp = Date.parse(initialValues?.releaseDate);
    return !isNaN(timestamp) ? new Date(timestamp) : newYorkMidnight(true);
  };

  return {
    [FIELDS.TITLE]: initialValues?.[FIELDS.TITLE] ?? '',
    [FIELDS.ABOUT]: initialValues?.[FIELDS.ABOUT] ?? '',
    [FIELDS.DURATION]: initialType === FILE_TYPE.VIDEO ? (initialValues?.[FIELDS.DURATION] ?? '') : '',
    [FIELDS.PAGES]: initialType === FILE_TYPE.PDF ? (initialValues?.[FIELDS.PAGES] ?? '') : '',
    [FIELDS.TAGS]: initialValues?.[FIELDS.TAGS] ?? [],
    [FIELDS.THUMBNAIL_LINK]:
      initialValues?.[FIELDS.THUMBNAIL_LINK] ?? initialValues?.[API_KEYS.THUMBNAIL_LINK] ?? '',
    [FIELDS.PRODUCTS]: initialValues?.[FIELDS.PRODUCTS] ?? [],
    [FIELDS.INSTRUCTOR]: initialValues?.[FIELDS.INSTRUCTOR] ?? '',
    [FIELDS.CATEGORY]: initialValues?.[FIELDS.CATEGORY] ?? [],
    [FIELDS.FILE_TYPE]: initialType,
    [FIELDS.FILE_URL]: initialValues?.[FIELDS.FILE_URL] ?? initialValues?.file_url ?? '',
    [FIELDS.RELATED]: initialValues?.[FIELDS.RELATED]?.map((r) => r.referralId) ?? [],
    [FIELDS.DATE_ONLY]: getReleaseDate()
  };
};

const buildPayload = (form, isVideo) => {
  const base = {
    [FIELDS.TITLE]: form[FIELDS.TITLE],
    [FIELDS.ABOUT]: form[FIELDS.ABOUT],
    [FIELDS.TAGS]: form[FIELDS.TAGS],
    [FIELDS.THUMBNAIL_LINK]: form[FIELDS.THUMBNAIL_LINK],
    [FIELDS.PRODUCTS]: form[FIELDS.PRODUCTS],
    [FIELDS.INSTRUCTOR]: form[FIELDS.INSTRUCTOR],
    [FIELDS.CATEGORY]: form[FIELDS.CATEGORY],
    [FIELDS.FILE_TYPE]: form[FIELDS.FILE_TYPE],
    [FIELDS.FILE_URL]: form[FIELDS.FILE_URL],
    [FIELDS.RELATED]: form[FIELDS.RELATED],
    [FIELDS.CATEGORY]: Array.isArray(form[FIELDS.CATEGORY]) ? form[FIELDS.CATEGORY] : [form[FIELDS.CATEGORY]],
    [FIELDS.DATE_ONLY]: form[FIELDS.DATE_ONLY],
  };

  return isVideo
    ? { ...base, [FIELDS.DURATION]: Number(form[FIELDS.DURATION]) || 0 }
    : { ...base, [FIELDS.PAGES]: Number(form[FIELDS.PAGES]) || 0 };
};

const validateForm = (form) => {
  if (!form[FIELDS.TITLE].trim()) return MESSAGES.ERROR_TITLE_REQUIRED;
  if (isBlankRichText(form[FIELDS.ABOUT])) return MESSAGES.ERROR_ABOUT_REQUIRED;
  if (!form[FIELDS.FILE_TYPE]) return MESSAGES.ERROR_FILETYPE_REQUIRED;

  if (form[FIELDS.FILE_TYPE] === FILE_TYPE.VIDEO) {
    if (!form[FIELDS.FILE_URL].trim()) return MESSAGES.ERROR_VIDEO_EMPTY;
    const durNum = Number(form[FIELDS.DURATION]);
    if (!Number.isFinite(durNum) || durNum <= 0) return MESSAGES.ERROR_DURATION_INVALID;
  }

  if (form[FIELDS.FILE_TYPE] === FILE_TYPE.PDF) {
    if (!form[FIELDS.FILE_URL].trim()) return MESSAGES.ERROR_PDF_EMPTY;
    const pagesNum = Number(form[FIELDS.PAGES]);
    if (!Number.isFinite(pagesNum) || pagesNum <= 0) return MESSAGES.ERROR_PAGES_INVALID;
  }

  return null;
};


const RichTextEditor = ({ value, onChange, label = UI_LABELS.ABOUT }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {label}
    </Typography>
    <ReactQuill value={value} onChange={onChange} theme="snow" modules={{
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
      ],
    }} />
  </Box>
);


const ProduceTRCContent = ({
  mode = 'create',
  initialValues = null,
  onSubmit = async () => { },
  labels = {},
}) => {
  const user = useAtomValue(userInfoAtom);
  const isEdit = mode === 'edit';

  const initialForm = useMemo(() => pickInitialForm(initialValues), [initialValues]);

  const [form, setForm] = useState(initialForm);
  const [isPolling, setIsPolling] = useState(false);
  const [releaseDate, setReleaseDate] = useState(new Date(form[FIELDS.DATE_ONLY]))

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  console.log(form.related, "********form**********")


  const handleVideoReady = (muxVideoLink, durationSeconds) => {
    if (muxVideoLink) setField(FIELDS.FILE_URL, muxVideoLink);
    if (Number.isFinite(durationSeconds)) setField(FIELDS.DURATION, Math.floor(durationSeconds));
    setIsPolling(false);
  };

  const handleMuxUploadStart = () => setIsPolling(true);

  const handleMuxUploadError = (err) => {
    setIsPolling(false);
    if (err) toast.error(MESSAGES.MUX_UPLOAD_FAILED);
  };

  const handleFileTypeChange = (e) => {
    const nextType = e.target.value;
    setForm((prev) => ({
      ...prev,
      [FIELDS.FILE_TYPE]: nextType,
      [FIELDS.DURATION]: nextType === FILE_TYPE.VIDEO ? prev[FIELDS.DURATION] : '',
      [FIELDS.PAGES]: nextType === FILE_TYPE.PDF ? prev[FIELDS.PAGES] : '',
    }));
  };

  const handlePdfUrlChange = async (url) => {
    setField(FIELDS.FILE_URL, url);
    if (!url) return;

    try {
      const res = await fetch(url);
      const text = await res.text();
      const matches = text.match(PDF_PAGE_REGEX);
      const pageCount = matches ? matches.length : 0;
      setField(FIELDS.PAGES, pageCount);
    } catch (err) {
      toast.warn(MESSAGES.WARN_PDF_FETCH + (err?.message ? ` (${err.message})` : ''));
      setField(FIELDS.PAGES, '');
    }
  };

  const onReleaseDateChange = date => {
    setReleaseDate(date);
    const dateOnly = date ? dayjs(date).format('YYYY-MM-DD') : '';
    // Store only a calendar date; backend will convert to > 00:00 America/New_York > UTC
    setField(FIELDS.DATE_ONLY, dateOnly);
  };

  const handleSubmit = async () => {
    const err = validateForm(form);
    if (err) {
      toast.error(err);
      return;
    }

    const isVideo = form[FIELDS.FILE_TYPE] === FILE_TYPE.VIDEO;
    const payload = buildPayload(form, isVideo);

    try {
      await onSubmit(payload);
      toast.success(
        (isEdit ? labels.successEdit : labels.successCreate) ??
        (isEdit ? MESSAGES.SUCCESS_EDIT : MESSAGES.SUCCESS_CREATE),
      );
    } catch (e) {
      toast.error(MESSAGES.ERROR_GENERIC + (e?.message ? ` (${e.message})` : ''));
    }
  };

  return (
    <Card>
      <CardContent>
        {isPolling && <div style={STYLES.OVERLAY}>{UI_LABELS.VIDEO_OVERLAY}</div>}

        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {labels.title ?? (isEdit ? UI_LABELS.PAGE_TITLE_EDIT : UI_LABELS.PAGE_TITLE_CREATE)}
        </Typography>

        <Stack spacing={2}>
          <TextField
            label={UI_LABELS.TITLE}
            value={form[FIELDS.TITLE]}
            onChange={(e) => setField(FIELDS.TITLE, e.target.value)}
            fullWidth
          />

          <RichTextEditor
            value={form[FIELDS.ABOUT]}
            onChange={(val) => setField(FIELDS.ABOUT, val)}
          />

          <CategoriesDropdown
            value={form[FIELDS.CATEGORY]}
            onChange={(catId) => setField(FIELDS.CATEGORY, catId)}
          />

          <TagsDropdown
            value={form[FIELDS.TAGS]}
            onChange={(tags) => setField(FIELDS.TAGS, tags)}
          />

          <ProductsDropdown
            value={form[FIELDS.PRODUCTS]}
            onChange={(products) => setField(FIELDS.PRODUCTS, products)}
          />

          <InstructorDropdown
            value={form[FIELDS.INSTRUCTOR]}
            onChange={(instructor) => setField(FIELDS.INSTRUCTOR, instructor)}
            token={user?.token}
          />
          <RelatedVideosDropdown
            value={form[FIELDS.RELATED]}
            onChange={(relatedIds) => setField(FIELDS.RELATED, relatedIds)}
          />


          <FileUploadWithManualInput
            label={UI_LABELS.THUMBNAIL_URL}
            accept="image/*"
            currentUrl={form[FIELDS.THUMBNAIL_LINK]}
            onChange={(url) => setField(FIELDS.THUMBNAIL_LINK, url)}
            onUpload={(url) => setField(FIELDS.THUMBNAIL_LINK, url)}
            placeholder="Enter thumbnail URL or upload image"
          />

          <FormControl fullWidth>
            <InputLabel id="file-type-label">{UI_LABELS.FILE_TYPE}</InputLabel>
            <Select
              labelId="file-type-label"
              label={UI_LABELS.FILE_TYPE}
              value={form[FIELDS.FILE_TYPE]}
              onChange={handleFileTypeChange}
            >
              {FILE_TYPE_OPTIONS.map((ft) => (
                <MenuItem key={ft.value} value={ft.value}>
                  {ft.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {form[FIELDS.FILE_TYPE] === FILE_TYPE.VIDEO ? (
            <MuxFileUpload
              title={form[FIELDS.TITLE]}
              fileUrl={form[FIELDS.FILE_URL]}
              onFileUrlChange={(url) => setField(FIELDS.FILE_URL, url)}
              onVideoReady={handleVideoReady}
              onUploadStart={handleMuxUploadStart}
              onError={handleMuxUploadError}
            />
          ) : (
            <FileUploadWithManualInput
              label={UI_LABELS.PDF_URL}
              accept="application/pdf"
              currentUrl={form[FIELDS.FILE_URL]}
              onChange={handlePdfUrlChange}
              onUpload={handlePdfUrlChange}
              placeholder="Enter PDF URL or upload file"
            />
          )}

          {form[FIELDS.FILE_TYPE] === FILE_TYPE.VIDEO ? (
            <TextField
              label={UI_LABELS.DURATION}
              type="number"
              inputProps={{ min: 0 }}
              value={form[FIELDS.DURATION]}
              onChange={(e) => setField(FIELDS.DURATION, e.target.value)}
              fullWidth
            />
          ) : (
            <TextField
              label={UI_LABELS.PAGES}
              type="number"
              inputProps={{ min: 1 }}
              value={form[FIELDS.PAGES]}
              onChange={(e) => setField(FIELDS.PAGES, e.target.value)}
              helperText={UI_LABELS.PAGES_HELPER}
              fullWidth
            />
          )}

          <ReleaseSchedule
            releaseDate={releaseDate}
            onReleaseDateChange={onReleaseDateChange}
            newYorkMidnight={newYorkMidnight}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button variant="contained" onClick={handleSubmit}>
              {labels.submit ?? (isEdit ? UI_LABELS.BTN_UPDATE : UI_LABELS.BTN_SAVE)}
            </Button>
            <Button variant="outlined" onClick={() => setForm(getEmptyForm())}>
              {labels.reset ?? UI_LABELS.BTN_RESET}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProduceTRCContent;
